import { useState, useEffect, useRef } from "react";
import { useAuth }     from "@context/AuthContext";
import { useNavigate } from "react-router-dom";
import cache           from "@utils/cache";
import axios           from "axios";
import { API_BASE, PROFILE_IMG_BASE } from "@config/env";


const COLORS = ["#2563EB","#16A34A","#F59E0B","#EC4899","#06B6D4","#7C3AED"];
const post   = (ep, fields) => { const fd=new FormData(); Object.entries(fields).forEach(([k,v])=>fd.append(k,v)); return axios.post(`${API_BASE}${ep}`,fd).then(r=>r.data).catch(()=>null); };

function SkeletonRow() {
  return (
    <tr style={{ borderBottom:"1px solid #F3F4F6" }}>
      <td style={{ padding:"14px" }}><div style={{ display:"flex", alignItems:"center", gap:10 }}><div style={{ width:38, height:38, borderRadius:9, background:"#F3F4F6", flexShrink:0 }} /><div><div style={{ height:13, background:"#F3F4F6", borderRadius:5, width:140, marginBottom:6 }} /><div style={{ height:10, background:"#F8FAFC", borderRadius:5, width:100 }} /></div></div></td>
      <td style={{ padding:"14px" }}><div style={{ height:11, background:"#F3F4F6", borderRadius:5, width:160 }} /></td>
      <td style={{ padding:"14px" }}><div style={{ height:11, background:"#F3F4F6", borderRadius:5, width:80 }} /></td>
      <td style={{ padding:"14px" }}><div style={{ height:28, background:"#F3F4F6", borderRadius:8, width:80 }} /></td>
    </tr>
  );
}

export default function ResourceManagersPage() {
  const { userSlug } = useAuth();
  const navigate = useNavigate();

  const [managers, setManagers] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const fetchedRef = useRef(false);
  const CACHE_KEY  = `managers_${userSlug}`;

  useEffect(() => {
    if (!userSlug || fetchedRef.current) return;
    fetchedRef.current = true;
    const cached = cache.get(CACHE_KEY);
    if (cached) { setManagers(cached); setLoading(false); return; }
    post("get-my-resource-manager-listing", { user_slug: userSlug }).then(data => {
      const list = data?.resourceManagerList || data?.data || [];
      cache.set(CACHE_KEY, list);
      setManagers(Array.isArray(list) ? list : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [userSlug]);

  return (
    <div>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:800, color:"#111827", margin:0 }}>My Resource Managers</h1>
          <p style={{ fontSize:13, color:"#6B7280", margin:"3px 0 0" }}>Managers with access to your firm's resources</p>
        </div>
        <button onClick={() => navigate("/add-resource-manager")} className="fc-btn fc-btn-md fc-btn-primary fc-btn-pill">
          <i className="fa-solid fa-user-plus" style={{fontSize:12}} /> Add Manager
        </button>
      </div>

      <div style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:16, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ background:"#F8FAFC", borderBottom:"1px solid #E5E7EB" }}>
                {["Name & Role","Email","Resources","Actions"].map(h => (
                  <th key={h} style={{ padding:"11px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:"#6B7280", textTransform:"uppercase", letterSpacing:"0.06em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(4)].map((_,i) => <SkeletonRow key={i} />)
              ) : managers.length === 0 ? (
                <tr><td colSpan={4} style={{ padding:"52px 24px", textAlign:"center" }}>
                  <div style={{ width:48, height:48, borderRadius:12, background:"#F3F4F6", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 12px" }}>
                    <i className="fa-solid fa-users" style={{ fontSize:20, color:"#D1D5DB" }} />
                  </div>
                  <div style={{ fontSize:14, fontWeight:700, color:"#111827", marginBottom:4 }}>No managers yet</div>
                  <div style={{ fontSize:12, color:"#9CA3AF", marginBottom:16 }}>Add your first resource manager to get started</div>
                  <button onClick={() => navigate("/add-resource-manager")} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill">
                    <i className="fa-solid fa-user-plus" style={{fontSize:11}} /> Add Manager
                  </button>
                </td></tr>
              ) : managers.map((m, i) => {
                const name     = m.full_name || m.resource_name || `${m.first_name||""} ${m.last_name||""}`.trim();
                const initials = name.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase();
                const role     = m.user_profile_role || m.role || "";
                const email    = m.contact_email || m.email || m.user_email || "";
                const count    = m.resource_count ?? m.resourceCount ?? 0;
                const color    = COLORS[i % COLORS.length];
                return (
                  <tr key={m.user_slug || i}
                    style={{ borderBottom: i < managers.length-1 ? "1px solid #F3F4F6" : "none", transition:"background 0.12s" }}
                    onMouseEnter={e=>e.currentTarget.style.background="#FAFBFF"}
                    onMouseLeave={e=>e.currentTarget.style.background=""}>
                    <td style={{ padding:"12px 14px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:38, height:38, borderRadius:9, background:color+"18", border:`2px solid ${color}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:800, color, flexShrink:0 }}>{initials}</div>
                        <div>
                          <div style={{ fontWeight:700, color:"#111827" }}>{name}</div>
                          {role && <div style={{ fontSize:11, color:"#2563EB" }}>{role}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:"12px 14px", color:"#6B7280" }}>{email || "—"}</td>
                    <td style={{ padding:"12px 14px" }}>
                      <span style={{ fontSize:12, fontWeight:700, color:"#2563EB", background:"#EFF6FF", padding:"2px 9px", borderRadius:99 }}>{count}</span>
                    </td>
                    <td style={{ padding:"12px 14px" }}>
                      <button onClick={() => navigate(`/resources/details/${m.user_slug}`)}
                        style={{ padding:"6px 12px", borderRadius:7, border:"none", background:"#FEF3C7", color:"#92400E", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                        View Profile
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
